import React, { useState, useMemo } from 'react';
import {
  FiCalendar,
  FiBarChart2,
  FiTrendingUp,
  FiRefreshCw,
  FiClipboard
} from 'react-icons/fi';

// Utility function to calculate days difference
const getDaysUntilDeadline = (deadline) => {
  const now = new Date();
  const deadlineDate = new Date(deadline);
  const diffTime = deadlineDate - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

// 1. Línea de Tiempo de Plazos
export const DeadlineTimeline = ({ briefs = [] }) => {
  const [hoveredBrief, setHoveredBrief] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  // Sort and process briefs for timeline
  const processedBriefs = useMemo(() => {
    if (!Array.isArray(briefs) || briefs.length === 0) return [];
    
    return briefs
      .map(brief => {
        const daysRemaining = getDaysUntilDeadline(brief.deadline);
        let colorClass = 'safe'; // Verde por defecto
        let bgColor = '#10b981';
        
        if (daysRemaining < 3) {
          colorClass = 'urgent';
          bgColor = '#ef4444'; // Rojo si <3 días
        } else if (daysRemaining < 7) {
          colorClass = 'warning';
          bgColor = '#FF5500'; // Naranja Vissory si <7 días
        }
        
        return {
          ...brief,
          daysRemaining,
          colorClass,
          bgColor,
          truncatedTitle: brief.title.length > 20 ? brief.title.substring(0, 20) + '...' : brief.title
        };
      })
      .sort((a, b) => a.daysRemaining - b.daysRemaining)
      .slice(0, 7); // Máximo 7 elementos para evitar overflow
  }, [briefs]);

  const handleMouseEnter = (brief, event) => {
    setHoveredBrief(brief);
    setTooltipPosition({ x: event.clientX, y: event.clientY });
  };

  const handleMouseLeave = () => {
    setHoveredBrief(null);
  };

  const allOnTime = processedBriefs.length > 0 && processedBriefs.every(b => b.daysRemaining >= 0);
  const formatDeadlineDate = (deadline) => {
    if (!deadline) return '';
    const d = new Date(deadline);
    return d.toLocaleDateString('es-ES', { month: 'short', day: 'numeric' });
  };
  // Relleno = progreso desde inicio hasta hoy; barra va de start_date a deadline
  const getBarPercentFromDates = (startDate, deadline) => {
    if (!deadline) return 0;
    const end = new Date(deadline);
    const start = startDate ? new Date(startDate) : new Date(end.getTime() - 30 * 24 * 60 * 60 * 1000);
    const now = new Date();
    if (now <= start) return 0;
    if (now >= end) return 100;
    const total = end.getTime() - start.getTime();
    const elapsed = now.getTime() - start.getTime();
    return Math.round((elapsed / total) * 100);
  };
  // Colores por fila como en la imagen: naranja, azul, verde
  const timelineBarColors = ['#ff5500', '#4285F4', '#10b981'];
  const getBarColor = (index) => timelineBarColors[index % timelineBarColors.length];

  if (!briefs || briefs.length === 0) {
    return (
      <div className="widget-card widget-card-visual widget-card-timeline">
        <div className="widget-card-header widget-card-header-timeline">
          <div className="widget-card-icon-wrap">
            <FiCalendar aria-hidden="true" />
          </div>
          <div className="widget-card-title-block">
            <h3 className="widget-card-title">Línea de Tiempo</h3>
            <p className="widget-card-subtitle">Plazos de entrega</p>
          </div>
        </div>
        <div className="widget-empty">
          <FiCalendar className="widget-empty-icon" aria-hidden="true" />
          <p>No hay fases de proyectos con plazos</p>
        </div>
      </div>
    );
  }

  return (
    <div className="widget-card widget-card-visual widget-card-timeline">
      <div className="widget-card-header widget-card-header-timeline">
        <div className="widget-card-icon-wrap">
          <FiCalendar aria-hidden="true" />
        </div>
        <div className="widget-card-title-block">
          <h3 className="widget-card-title">Línea de Tiempo</h3>
          <p className="widget-card-subtitle">Plazos de entrega</p>
        </div>
        {allOnTime && (
          <span className="widget-card-badge widget-card-badge-timeline">
            <span className="widget-card-badge-dot" /> Al día
          </span>
        )}
      </div>

      <div className="widget-timeline-list">
        {processedBriefs.slice(0, 5).map((brief, index) => (
          <div
            key={brief.id}
            className="widget-timeline-row"
            onMouseEnter={(e) => handleMouseEnter(brief, e)}
            onMouseLeave={handleMouseLeave}
          >
            <span className="widget-timeline-name" title={brief.title}>{brief.truncatedTitle}</span>
            <div className="widget-timeline-bar-wrap">
              <div
                className="widget-timeline-bar-fill"
                style={{
                  width: `${getBarPercentFromDates(brief.start_date, brief.deadline)}%`,
                  backgroundColor: getBarColor(index)
                }}
              />
            </div>
            <span className="widget-timeline-date">{formatDeadlineDate(brief.deadline)}</span>
          </div>
        ))}
      </div>

      {hoveredBrief && (
        <div
          className="timeline-tooltip"
          style={{
            left: Math.min(tooltipPosition.x + 10, window.innerWidth - 200),
            top: tooltipPosition.y - 60
          }}
          role="tooltip"
          aria-hidden="false"
        >
          <div className="tooltip-title">{hoveredBrief.title}</div>
          <div className="tooltip-subtitle" style={{ color: hoveredBrief.bgColor }}>
            {hoveredBrief.daysRemaining > 0
              ? `${hoveredBrief.daysRemaining} días restantes`
              : `Vencido hace ${Math.abs(hoveredBrief.daysRemaining)} días`
            }
          </div>
        </div>
      )}
    </div>
  );
};

// 2. Tasa de Conversión - diseño exacto (cabecera azul oscuro, 3 métricas, 7 barras: 4ª azul, 7ª naranja)
const BAR_COUNT = 7;
const BAR_COLOR_GRAY = '#3C3C3C';
const BAR_COLOR_BLUE = '#4285F4';
const BAR_COLOR_ORANGE = '#ff5500';

export const ConversionRateChart = ({ data = [], totalProposals, acceptedProposals }) => {
  const conversionRate = (totalProposals != null && acceptedProposals != null && totalProposals > 0)
    ? Math.round((acceptedProposals / totalProposals) * 100)
    : (data.length > 0 ? Math.round(data.reduce((s, i) => s + (i.conversionRate || 0), 0) / data.length) : 0);
  const showMetrics = totalProposals != null && acceptedProposals != null;

  // 7 barras: alturas desde data (últimos 7 conversionRate) o tendencia placeholder
  const barsData = useMemo(() => {
    const values = Array.isArray(data) && data.length > 0
      ? data.slice(-BAR_COUNT).map((i) => i.conversionRate ?? 0)
      : [20, 35, 28, 55, 65, 58, conversionRate || 50];
    const padded = [...values];
    while (padded.length < BAR_COUNT) padded.unshift(0);
    return padded.slice(-BAR_COUNT).map((value, i) => ({ value, index: i }));
  }, [data, conversionRate]);

  const getBarFill = (index) => {
    if (index === 3) return BAR_COLOR_BLUE;
    if (index === 6) return BAR_COLOR_ORANGE;
    return BAR_COLOR_GRAY;
  };

  if (!data || data.length === 0) {
    return (
      <div className="widget-card widget-card-visual widget-card-conversion">
        <div className="widget-card-header widget-card-header-conversion">
          <div className="widget-card-icon-wrap">
            <FiBarChart2 aria-hidden="true" />
          </div>
          <div className="widget-card-title-block">
            <h3 className="widget-card-title">Tasa de Conversión</h3>
            <p className="widget-card-subtitle">Propuestas aceptadas</p>
          </div>
        </div>
        <div className="widget-empty">
          <FiTrendingUp className="widget-empty-icon" aria-hidden="true" />
          <p>No hay datos de conversión disponibles</p>
        </div>
      </div>
    );
  }

  return (
    <div className="widget-card widget-card-visual widget-card-conversion">
      <div className="widget-card-header widget-card-header-conversion">
        <div className="widget-card-icon-wrap">
          <FiBarChart2 aria-hidden="true" />
        </div>
        <div className="widget-card-title-block">
          <h3 className="widget-card-title">Tasa de Conversión</h3>
          <p className="widget-card-subtitle">Propuestas aceptadas</p>
        </div>
        {showMetrics && (
          <span className="widget-card-badge widget-card-badge-trend">
            <FiTrendingUp aria-hidden="true" /> +{conversionRate}%
          </span>
        )}
      </div>

      {showMetrics && (
        <div className="widget-metrics-row widget-metrics-conversion">
          <div className="widget-metric widget-metric-main">
            <span className="widget-metric-value widget-metric-value-main">{conversionRate}%</span>
            <span className="widget-metric-label">Tasa actual</span>
          </div>
          <div className="widget-metric">
            <span className="widget-metric-value">{totalProposals ?? 0}</span>
            <span className="widget-metric-label">Propuestas enviadas</span>
          </div>
          <div className="widget-metric">
            <span className="widget-metric-value">{acceptedProposals ?? 0}</span>
            <span className="widget-metric-label">Aceptadas</span>
          </div>
        </div>
      )}

      <div className="widget-chart-wrap widget-conversion-bars-wrap">
        <div className="widget-conversion-bars" role="img" aria-label="Evolución de la tasa de conversión">
          {barsData.map((item, i) => (
            <div
              key={i}
              className="widget-conversion-bar"
              style={{
                height: `${Math.max(8, Math.min(100, item.value))}%`,
                backgroundColor: getBarFill(i)
              }}
              title={`${item.value}%`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

// 3. Estado de Proyectos (lista + total + barra apilada)
export const ProjectStatusDonut = ({ data = [] }) => {
  const COLORS = {
    'Completados': '#10B981',
    'En Revisión': '#ff5500',
    'Activos': '#ff8844'
  };
  const totalProjects = useMemo(() => data.reduce((sum, entry) => sum + entry.value, 0), [data]);

  if (!data || data.length === 0 || totalProjects === 0) {
    return (
      <div className="widget-card widget-card-visual">
        <div className="widget-card-header widget-card-header-success">
          <div className="widget-card-icon-wrap">
            <FiRefreshCw aria-hidden="true" />
          </div>
          <div className="widget-card-title-block">
            <h3 className="widget-card-title">Estado</h3>
            <p className="widget-card-subtitle">De proyectos</p>
          </div>
        </div>
        <div className="widget-empty">
          <FiClipboard className="widget-empty-icon" aria-hidden="true" />
          <p>No hay proyectos disponibles</p>
        </div>
      </div>
    );
  }

  return (
    <div className="widget-card widget-card-visual">
      <div className="widget-card-header widget-card-header-success">
        <div className="widget-card-icon-wrap">
          <FiRefreshCw aria-hidden="true" />
        </div>
        <div className="widget-card-title-block">
          <h3 className="widget-card-title">Estado</h3>
          <p className="widget-card-subtitle">De proyectos</p>
        </div>
      </div>

      <ul className="widget-status-list">
        {data.map((entry, index) => (
          <li key={entry.name || index} className="widget-status-row">
            <span className="widget-status-dot" style={{ backgroundColor: COLORS[entry.name] || '#6b7280' }} />
            <span className="widget-status-label">{entry.name}</span>
            <span className="widget-status-value">{entry.value}</span>
          </li>
        ))}
      </ul>

      <div className="widget-status-total-row">
        <div className="widget-status-total-block">
          <span className="widget-status-total-value">{totalProjects}</span>
          <span className="widget-status-total-label">Total</span>
        </div>
        <div className="widget-status-bars-column">
          {data.map((entry, index) => (
            <div key={entry.name || index} className="widget-status-bar-row">
              <div className="widget-status-bar-track">
                <div
                  className="widget-status-bar-fill"
                  style={{
                    width: `${totalProjects > 0 ? (entry.value / totalProjects) * 100 : 0}%`,
                    backgroundColor: COLORS[entry.name] || '#6b7280'
                  }}
                  title={`${entry.name}: ${entry.value}`}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};